// Before/after diff for admin audit rows (no dependencies). Objects are flattened to dotted paths;
// arrays and primitives are compared as whole values so a reordered permission list reads as one
// changed line rather than many.

export type AuditDiffChange = 'added' | 'removed' | 'changed' | 'unchanged';

export type AuditDiffRow = {
  path: string;
  before: unknown;
  after: unknown;
  change: AuditDiffChange;
};

const MISSING = Symbol('missing');

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function flatten(value: unknown, prefix: string, out: Map<string, unknown>): void {
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0 && prefix) {
      out.set(prefix, {});
      return;
    }
    for (const key of keys) flatten(value[key], prefix ? `${prefix}.${key}` : key, out);
    return;
  }
  out.set(prefix || '(value)', value);
}

function stableStringify(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (isPlainObject(v)) {
    return `{${Object.keys(v)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(v[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v) ?? 'undefined';
}

/** Row-per-path diff of two audit states, sorted by path. `null`/absent states are treated as empty. */
export function computeAuditDiff(before: unknown, after: unknown): AuditDiffRow[] {
  const left = new Map<string, unknown>();
  const right = new Map<string, unknown>();
  if (before != null) flatten(before, '', left);
  if (after != null) flatten(after, '', right);
  const paths = [...new Set([...left.keys(), ...right.keys()])].sort();
  return paths.map((path) => {
    const b = left.has(path) ? left.get(path) : MISSING;
    const a = right.has(path) ? right.get(path) : MISSING;
    let change: AuditDiffChange;
    if (b === MISSING) change = 'added';
    else if (a === MISSING) change = 'removed';
    else change = stableStringify(a) === stableStringify(b) ? 'unchanged' : 'changed';
    return {
      path,
      before: b === MISSING ? undefined : b,
      after: a === MISSING ? undefined : a,
      change,
    };
  });
}

/** Counts per change kind (for a one-line summary). */
export function summarizeAuditDiff(rows: readonly AuditDiffRow[]): Record<AuditDiffChange, number> {
  const out: Record<AuditDiffChange, number> = { added: 0, removed: 0, changed: 0, unchanged: 0 };
  for (const r of rows) out[r.change] += 1;
  return out;
}

/** For arrays of primitives: what was added and removed (e.g. a role's permission list). */
export function diffStringArrays(
  before: unknown,
  after: unknown,
): { added: string[]; removed: string[] } | null {
  const isStrArr = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
  if (!isStrArr(before) || !isStrArr(after)) return null;
  const b = new Set(before);
  const a = new Set(after);
  return {
    added: [...a].filter((x) => !b.has(x)).sort(),
    removed: [...b].filter((x) => !a.has(x)).sort(),
  };
}

/** Compact display of one value in the diff viewer. */
export function formatAuditDiffValue(v: unknown): string {
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  return JSON.stringify(v, null, 2) ?? String(v);
}
